import type { ChangeEvent, FocusEvent, KeyboardEvent, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export function useAutocomplete(
  inputRef: RefObject<HTMLInputElement | null>,
  openOnFocus: boolean,
  shouldShowSuggestions: (inputValue: string) => boolean,
  suggestions: string[],
  onSuggestionSelected?: (suggestion: string) => void,
) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const closeSuggestions = useCallback(() => {
    setShowSuggestions(false);
    setActiveIndex(null);
  }, []);

  const handleInputFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (openOnFocus && shouldShowSuggestions(e.target.value)) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    closeSuggestions();
    e.target.removeAttribute('aria-activedescendant');
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (inputRef?.current) {
      if (!shouldShowSuggestions(e.target.value)) {
        closeSuggestions();
      }
    }
  };

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      if (inputRef.current) {
        // oxlint-disable-next-line no-param-reassign
        inputRef.current.value = suggestion;

        const customEvent = new CustomEvent('change', {
          detail: { value: suggestion },
        });
        inputRef?.current.dispatchEvent(customEvent);

        inputRef.current.focus();

        if (onSuggestionSelected) {
          onSuggestionSelected(suggestion);
        }
      }

      closeSuggestions();
    },
    [closeSuggestions, inputRef, onSuggestionSelected],
  );

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => {
          if (prev === null) {
            return 0;
          }

          return (prev + 1) % suggestions.length;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => {
          if (prev === null) {
            return suggestions.length - 1;
          }

          return (prev - 1 + suggestions.length) % suggestions.length;
        });
        break;
      case 'Enter':
        if (activeIndex !== null) {
          e.preventDefault();
          handleSuggestionClick(suggestions[activeIndex]);
        }
        break;
      case 'Escape':
        closeSuggestions();
        if (inputRef.current) {
          inputRef.current.focus();
        }
        break;
    }
  };

  useEffect(() => {
    if (activeIndex !== null && activeIndex >= suggestions.length) {
      setActiveIndex(null);
    }
  }, [activeIndex, suggestions.length]);

  return {
    activeIndex,
    showSuggestions,
    closeSuggestions,
    suggestionsRef,
    handleInputChange,
    handleInputBlur,
    handleInputFocus,
    handleInputKeyDown,
    handleSuggestionClick,
  };
}
